import React from "react";
import cn from "classnames";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Card from "../../../components/Card";
import Icon from "../../../components/Icon";
import styles from "./Inbox.module.sass";

const timeAgo = (dateStr) => {
  try {
    const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
    if (diff < 3600) return `${Math.max(1, Math.round(diff / 60))} min`;
    if (diff < 86400) return `${Math.round(diff / 3600)} h`;
    return `${Math.round(diff / 86400)} d`;
  } catch {
    return "";
  }
};

/**
 * Bandeja de pendientes del Home: último bug sin resolver, último feedback sin
 * contestar y última palabra con contexto por revisar, con el total de cada cola.
 */
const Inbox = ({ className }) => {
  const { t } = useTranslation();
  const { value, isLoading, error } = useSelector((s) => s.stats.inbox);

  const rows = [
    {
      key: "bugs",
      to: "/bug-reports",
      icon: "info-stroke",
      color: "#FFBC99",
      title: t("home.inbox.bugs", "Bugs sin resolver"),
      count: value?.bugs?.pending,
      snippet: value?.bugs?.latest?.reason,
      meta: [
        value?.bugs?.latest?.reportedBy,
        value?.bugs?.latest?.system,
        value?.bugs?.latest?.date && timeAgo(value.bugs.latest.date),
      ],
    },
    {
      key: "feedback",
      to: "/feedback",
      icon: "message",
      color: "#B5E4CA",
      title: t("home.inbox.feedback", "Feedback sin contestar"),
      count: value?.feedback?.pending,
      snippet: value?.feedback?.latest?.content,
      meta: [
        value?.feedback?.latest?.reportedBy,
        value?.feedback?.latest?.feedbackType,
        value?.feedback?.latest?.date && timeAgo(value.feedback.latest.date),
      ],
    },
    {
      key: "words",
      to: value?.wordsToReview?.latest?._id
        ? `/card/edit/${value.wordsToReview.latest._id}`
        : "/cards",
      icon: "edit",
      color: "#CABDFF",
      title: t("home.inbox.words", "Palabras a revisar (contexto)"),
      count: value?.wordsToReview?.pending,
      snippet: value?.wordsToReview?.latest
        ? `${value.wordsToReview.latest.character ?? "—"}${
            value.wordsToReview.latest.pinyin ? ` · ${value.wordsToReview.latest.pinyin}` : ""
          }`
        : null,
      meta: [value?.wordsToReview?.latest?.date && timeAgo(value.wordsToReview.latest.date)],
    },
  ];

  const totalPending = rows.reduce((sum, r) => sum + (r.count || 0), 0);

  return (
    <Card
      className={cn(styles.card, className)}
      title={t("home.inbox.title", "Pendientes")}
      classTitle="title-red"
      head={
        totalPending > 0 ? (
          <div className={styles.headCounter}>
            {totalPending} {t("home.inbox.total", "en cola")}
          </div>
        ) : null
      }
    >
      <div className={styles.list}>
        {isLoading && !value && (
          <div className={styles.state}>{t("home.inbox.loading", "Cargando…")}</div>
        )}
        {error && <div className={styles.state}>{t("home.inbox.error", "No se pudo cargar")}</div>}
        {value &&
          rows.map((row) => (
            <Link key={row.key} to={row.to} className={styles.row}>
              <div className={styles.rowIcon} style={{ backgroundColor: row.color }}>
                <Icon name={row.icon} size="20" />
              </div>
              <div className={styles.rowBody}>
                <div className={styles.rowTitle}>
                  {row.title}
                  <span
                    className={cn(styles.badge, {
                      [styles.badgeZero]: !row.count,
                    })}
                  >
                    {row.count ?? 0}
                  </span>
                </div>
                {row.count > 0 && row.snippet ? (
                  <div className={styles.snippet}>“{String(row.snippet).slice(0, 90)}”</div>
                ) : (
                  <div className={styles.snippetEmpty}>
                    {t("home.inbox.clear", "Nada pendiente 🎉")}
                  </div>
                )}
                {row.count > 0 && (
                  <div className={styles.meta}>{row.meta.filter(Boolean).join(" · ")}</div>
                )}
              </div>
              <div className={styles.arrow}>
                <Icon name="arrow-next" size="20" />
              </div>
            </Link>
          ))}
      </div>
    </Card>
  );
};

export default Inbox;
